import React, { useState } from 'react';
import { X, Users, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Event, Member, Town } from '../../types';

interface EventAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  members: Member[];
  towns: Town[];
  onSaveAttendance: (eventId: string, attendance: Record<string, 'present' | 'absent'>) => void;
}

export const EventAttendanceModal: React.FC<EventAttendanceModalProps> = ({
  isOpen,
  onClose,
  event,
  members,
  towns,
  onSaveAttendance
}) => {
  const [attendance, setAttendance] = useState<Record<string, 'present' | 'absent'>>({});
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen || !event) return null;

  const town = towns.find(t => t.id === event.townId);
  const townMembers = members.filter(m => m.townId === event.townId);
  const filteredMembers = townMembers.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAttendanceChange = (memberId: string, status: 'present' | 'absent') => {
    setAttendance(prev => ({
      ...prev,
      [memberId]: status
    }));
  };

  const handleSave = () => {
    onSaveAttendance(event.id, attendance);
    onClose();
    setAttendance({});
    setSearchTerm('');
  };

  const presentCount = Object.values(attendance).filter(status => status === 'present').length;
  const absentCount = Object.values(attendance).filter(status => status === 'absent').length;
  const unmarkedCount = filteredMembers.length - presentCount - absentCount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2 text-purple-600" />
              Event Attendance
            </h2>
            <p className="text-sm text-gray-600 mt-1">{event.title} • {town?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Event Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-purple-600">Date & Time</p>
                <p className="font-medium text-purple-900">{event.date} at {event.time}</p>
              </div>
              <div>
                <p className="text-sm text-purple-600">Location</p>
                <p className="font-medium text-purple-900">{event.location}</p>
              </div>
            </div>
          </div>

          {/* Attendance Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
              <p className="text-sm text-green-600">Present</p>
              <p className="text-xl font-bold text-green-700">{presentCount}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
              <p className="text-sm text-red-600">Absent</p>
              <p className="text-xl font-bold text-red-700">{absentCount}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <Clock className="h-6 w-6 text-gray-600 mx-auto mb-1" />
              <p className="text-sm text-gray-600">Unmarked</p>
              <p className="text-xl font-bold text-gray-700">{unmarkedCount}</p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Member List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-500">{member.username} • {member.phone}</p>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAttendanceChange(member.id, 'present')}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      attendance[member.id] === 'present'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    Present
                  </button>
                  <button
                    onClick={() => handleAttendanceChange(member.id, 'absent')}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      attendance[member.id] === 'absent'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    Absent
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredMembers.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No members found for this town</p>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {presentCount + absentCount} of {filteredMembers.length} members marked
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
            >
              <Users className="h-4 w-4" />
              <span>Save Attendance</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};